import { CheckCircle, XCircle, X } from 'lucide-react'
import MultipleChoiceSolutionDisplay from '../quiz/MultipleChoiceSolutionDisplay'
import TrueFalseSolutionDisplay from '../quiz/TrueFalseSolutionDisplay'
import ShortAnswerSolutionDisplay from '../quiz/ShortAnswerSolutionDisplay'
import KaTeXRenderer from '../common/KaTeXRenderer'
import StudentAIReviewPanel from '../ai/StudentAIReviewPanel'

type Props = {
  detail: any
  onClose: () => void
}

const LETTERS = ['A', 'B', 'C', 'D']

const answerValue = (answer: any) => {
  if (answer == null || answer === '') return 'Chưa trả lời'
  if (typeof answer !== 'object') return String(answer)
  return String(
    answer.content
    ?? answer.selected_option_id
    ?? answer.option_id
    ?? answer.userAnswer
    ?? answer.original_letter
    ?? answer.letter
    ?? JSON.stringify(answer)
  )
}

/** Dựng lại đúng thứ tự phương án HỌC SINH ĐÃ THẤY (option_order) — đề CŨ không có
 * option_order thì giữ nguyên thứ tự lưu trong DB (snapshot.choices). */
const orderedChoices = (snapshot: any): { id: string; content: string }[] => {
  const raw = Array.isArray(snapshot?.choices) ? snapshot.choices : []
  const records = raw.map((choice: any, index: number) => (
    choice && typeof choice === 'object'
      ? { id: String(choice.id ?? choice.option_id ?? LETTERS[index]), content: String(choice.content ?? choice.text ?? '') }
      : { id: LETTERS[index], content: String(choice ?? '') }
  ))
  const order = Array.isArray(snapshot?.option_order) ? snapshot.option_order.map(String) : []
  if (!order.length) return records
  const byId = new Map(records.map((choice: any) => [choice.id, choice]))
  const ordered = order.map((id: string) => byId.get(id)).filter(Boolean)
  return ordered.length === records.length ? ordered : records
}

/** Chuyển 1 câu trong snapshot_json (AttemptAnswer) về đúng shape mà
 * MultipleChoiceSolutionDisplay/TrueFalseSolutionDisplay/ShortAnswerSolutionDisplay
 * đang dùng — TÁI SỬ DỤNG y hệt component render màn hình ngay sau khi Submit
 * (QuizSubmission.tsx), để lịch sử xem lại luôn khớp với lúc làm bài xong. */
const toQuestionProp = (item: any) => {
  const snapshot = item.snapshot || {}
  const choices = orderedChoices(snapshot)
  const q: any = {
    id: item.question_id,
    cau_hoi: snapshot.question_lead || snapshot.question || '',
    question_lead: snapshot.question_lead || snapshot.question || '',
    lua_chon: choices,
    dap_an_dung: snapshot.correct_answer,
    loi_giai: snapshot.explanation || null,
    diem: snapshot.max_points ?? item.max_points ?? 1,
    muc_do: snapshot.muc_do,
    hinh_anh: snapshot.hinh_anh || null,
    hinh_anh_giai_thich: snapshot.hinh_anh_giai_thich || null,
  }
  for (const s of (snapshot.statements || [])) {
    q[`statement_${s.key}`] = s.content
    q[`answer_${s.key}`] = s.correct
    q[`solution_${s.key}`] = s.solution
  }
  return q
}

const toAnalysisProp = (item: any) => ({
  isCorrect: !!item.is_correct,
  points: item.points,
  correctAnswer: item.snapshot?.correct_answer,
  explanation: item.snapshot?.explanation,
})

const toUserAnswerProp = (item: any, isChoice: boolean) => {
  if (!isChoice) return item.answer
  const a = item.answer
  if (a && typeof a === 'object') return a.selected_option_id ?? a.option_id ?? a.original_letter ?? a.letter ?? null
  return a
}

export default function PmakerAttemptDetailModal({ detail, onClose }: Props) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-3">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-[#fdfcf0] shadow-2xl">
        <div className="flex items-start justify-between border-b bg-white px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-800">{detail.title}</h2>
            <p className="text-sm text-slate-500">
              Điểm {detail.score}/{detail.maxScore ?? detail.max_score}{detail.variant_code ? ` · Mã đề ${detail.variant_code} (phiếu giấy)` : detail.mode === 'paper' ? ' · Phiếu giấy' : ''}{detail.subject ? ` · Môn ${detail.subject}` : ''}{detail.class_name ? ` · Lớp ${detail.class_name}` : ''}{detail.tab_switches ? ` · Rời màn hình ${detail.tab_switches} lần` : ''}
              {detail.student_name ? ` · ${detail.student_name}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100" aria-label="Đóng">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 overflow-y-auto p-4 sm:p-6">
          {detail.type === 'exam' && <StudentAIReviewPanel attemptId={detail.id} />}
          {(detail.questions || []).map((item: any, index: number) => {
            const snapshot = item.snapshot || {}
            const group = String(snapshot.group || snapshot.type || '').toUpperCase()
            const isChoice = group === 'TN' || group.includes('TRAC_NGHIEM') || group.includes('MULTIPLE_CHOICE')
            const isTrueFalse = group === 'DS' || group.includes('DUNG_SAI') || group.includes('TRUE_FALSE')
            const isShortAnswer = group === 'TL' || group.includes('TINH_TOAN') || group.includes('SHORT_ANSWER') || group.includes('TRA_LOI_NGAN')
            const question = toQuestionProp(item)
            const analysis = toAnalysisProp(item)

            return (
              <div key={item.question_id} className={`border rounded-lg p-4 ${item.is_correct ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                {/* Câu chùm: hiện phần chung ở câu đầu của cụm (bài nộp mới lưu cluster_context trong snapshot) */}
                {snapshot.cluster_context && detail.questions[index - 1]?.snapshot?.cluster_id !== snapshot.cluster_id && (
                  <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                    <p className="text-xs font-bold text-amber-700 mb-1">Phần chung (câu hỏi chùm)</p>
                    <KaTeXRenderer content={snapshot.cluster_context} />
                  </div>
                )}
                <div className="flex items-start justify-between mb-2 gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-700">Câu {index + 1}</span>
                    {question.muc_do && (
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${question.muc_do === 'M1' ? 'bg-green-100 text-green-800' : question.muc_do === 'M2' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                        {question.muc_do}
                      </span>
                    )}
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${isChoice ? 'bg-blue-100 text-blue-800' : isTrueFalse ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'}`}>
                      {isChoice ? 'Trắc nghiệm' : isTrueFalse ? 'Đúng/Sai' : isShortAnswer ? 'Tính toán' : 'Tự luận'}
                    </span>
                    {item.is_legacy && <span className="rounded-full bg-amber-100 px-2 py-1 text-sm text-amber-700">Dữ liệu cũ</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {item.is_correct ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className="text-base font-medium text-gray-600">{item.points}/{item.max_points} điểm</span>
                  </div>
                </div>

                {isChoice ? (
                  <MultipleChoiceSolutionDisplay question={question} userAnswer={toUserAnswerProp(item, true)} analysis={analysis} />
                ) : isTrueFalse ? (
                  <TrueFalseSolutionDisplay question={question} userAnswer={item.answer} analysis={analysis} />
                ) : isShortAnswer ? (
                  <ShortAnswerSolutionDisplay question={question} userAnswer={item.answer} analysis={analysis} />
                ) : (
                  <div className="mt-2 space-y-2">
                    <div className="bg-gray-50 rounded p-3">
                      <KaTeXRenderer content={question.cau_hoi || 'Không còn nội dung câu hỏi'} moveImagesToEnd />
                    </div>
                    <div className="space-y-1 rounded-lg border border-slate-200 bg-white p-3 text-sm">
                      <p><span className="font-semibold">Đã trả lời:</span> {answerValue(item.answer)}</p>
                      <p><span className="font-semibold">Đáp án:</span> {answerValue(snapshot.correct_answer)}</p>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
